import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Chip, ClickAwayListener, InputAdornment, Paper, Stack, TextField, Typography } from "@mui/material";
import { Search, Plus, Square, FolderKanban, Zap, Timer, Tag, MessageSquare, User } from "lucide-react";
import { T, TYPE, primaryButtonSx, ghostButtonSx, formatRelative } from "../user/complaints/shared";
import { pm } from "./api";
import { usePm } from "./PmContext";
import { TypeIcon } from "./ui";
import { fieldSx } from "./inputs";
import CreateIssueDialog from "./CreateIssueDialog";

const Section = ({ icon: Icon, title, children }) => (
  <Box sx={{ py: 0.75 }}>
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ px: 1.5, mb: 0.25 }}>
      <Icon size={12} color={T.inkMuted} />
      <Typography sx={{ fontSize: "0.66rem", fontWeight: 700, color: T.inkMuted, textTransform: "uppercase", letterSpacing: "0.4px" }}>{title}</Typography>
    </Stack>
    {children}
  </Box>
);
const Row = ({ onClick, children }) => (
  <Box onClick={onClick} sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.75, cursor: "pointer", "&:hover": { bgcolor: T.surfaceMuted } }}>{children}</Box>
);

/** Global search + create + running timer, shown on every PM screen. */
const PmTopBar = ({ project, onCreated }) => {
  const ctx = usePm();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [timer, setTimer] = useState(null);
  const [, tick] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (text.trim().length < 2) {
      setRes(null);
      return undefined;
    }
    const t = setTimeout(() => pm.search(text.trim()).then(setRes).catch(() => setRes(null)), 220);
    return () => clearTimeout(t);
  }, [text]);
  useEffect(() => {
    pm.myTimer().then(setTimer).catch(() => {});
    const i = setInterval(() => tick((x) => x + 1), 30000);
    const onKey = (e) => {
      if (e.key === "/" && !["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName) && !document.activeElement?.isContentEditable) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      clearInterval(i);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  const go = (url) => {
    setOpen(false);
    setText("");
    navigate(url);
  };
  const empty = res && !Object.values(res).some((v) => v?.length);

  return (
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2.5 }}>
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <Box sx={{ position: "relative", flexGrow: 1, maxWidth: 560 }}>
          <TextField inputRef={inputRef} fullWidth size="small" placeholder="Search issues, projects, sprints, people…  ( / )" value={text}
            onFocus={() => setOpen(true)} onChange={(e) => { setText(e.target.value); setOpen(true); }}
            onKeyDown={(e) => { if (e.key === "Enter" && /^[A-Z][A-Z0-9]+-\d+$/i.test(text.trim())) go(`/projects/${text.trim().split("-")[0].toUpperCase()}/issues/${text.trim().toUpperCase()}`); if (e.key === "Escape") setOpen(false); }}
            sx={fieldSx} InputProps={{ startAdornment: <InputAdornment position="start"><Search size={15} /></InputAdornment> }} />
          {open && res && (
            <Paper sx={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 20, maxHeight: 460, overflowY: "auto", borderRadius: "10px", border: `1px solid ${T.border}`, boxShadow: T.shadowHover }}>
              {empty && <Typography sx={{ p: 2, fontSize: TYPE.small, color: T.inkMuted }}>No results for “{text}”.</Typography>}
              {res.issues?.length > 0 && (
                <Section icon={Tag} title="Issues">
                  {res.issues.map((i) => (
                    <Row key={i._id} onClick={() => go(`/projects/${i.key.split("-")[0]}/issues/${i.key}`)}>
                      <TypeIcon typeId={i.typeId} size={14} />
                      <Typography sx={{ fontSize: TYPE.small, fontWeight: 600, whiteSpace: "nowrap" }}>{i.key}</Typography>
                      <Typography noWrap sx={{ fontSize: TYPE.small, color: T.inkSecondary }}>{i.summary}</Typography>
                    </Row>
                  ))}
                </Section>
              )}
              {res.epics?.length > 0 && (
                <Section icon={Zap} title="Epics">
                  {res.epics.map((i) => <Row key={i._id} onClick={() => go(`/projects/${i.key.split("-")[0]}/issues/${i.key}`)}><TypeIcon typeId={i.typeId} size={14} /><Typography noWrap sx={{ fontSize: TYPE.small }}>{i.key} · {i.summary}</Typography></Row>)}
                </Section>
              )}
              {res.projects?.length > 0 && (
                <Section icon={FolderKanban} title="Projects">
                  {res.projects.map((p) => <Row key={p._id} onClick={() => go(`/projects/${p.key}`)}><Typography sx={{ fontSize: TYPE.small, fontWeight: 600 }}>{p.key}</Typography><Typography noWrap sx={{ fontSize: TYPE.small }}>{p.name}</Typography></Row>)}
                </Section>
              )}
              {res.sprints?.length > 0 && (
                <Section icon={Timer} title="Sprints">
                  {res.sprints.map((s) => <Row key={s._id} onClick={() => go(`/projects/${s.projectKey}/sprints`)}><Typography noWrap sx={{ fontSize: TYPE.small }}>{s.name}</Typography><Chip size="small" label={s.status} sx={{ height: 18, fontSize: "0.64rem" }} /><Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{s.projectKey}</Typography></Row>)}
                </Section>
              )}
              {res.releases?.length > 0 && (
                <Section icon={Tag} title="Releases">
                  {res.releases.map((v) => <Row key={v._id} onClick={() => go(`/projects/${v.projectKey}/releases`)}><Typography noWrap sx={{ fontSize: TYPE.small }}>{v.name}</Typography><Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{v.projectKey} · {v.status}</Typography></Row>)}
                </Section>
              )}
              {res.comments?.length > 0 && (
                <Section icon={MessageSquare} title="Comments">
                  {res.comments.filter((c) => c.issue).map((c) => <Row key={c._id} onClick={() => go(`/projects/${c.issue.key.split("-")[0]}/issues/${c.issue.key}`)}><Typography sx={{ fontSize: TYPE.small, fontWeight: 600, whiteSpace: "nowrap" }}>{c.issue.key}</Typography><Typography noWrap sx={{ fontSize: TYPE.small, color: T.inkSecondary }}>{c.author?.name}: {c.bodyText}</Typography></Row>)}
                </Section>
              )}
              {res.people?.length > 0 && (
                <Section icon={User} title="People">
                  {res.people.map((p) => <Row key={String(p.id)} onClick={() => go(`/pm/issues?assignee=${p.id}`)}><Typography sx={{ fontSize: TYPE.small }}>{p.name}</Typography><Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{p.email} · open their issues</Typography></Row>)}
                </Section>
              )}
            </Paper>
          )}
        </Box>
      </ClickAwayListener>
      <Box sx={{ flexGrow: 1 }} />
      {timer?.issue && (
        <Chip color="error" variant="outlined" icon={<Square size={12} />} label={`${timer.issue.key} · ${formatRelative(timer.startedAt).replace(" ago", "")}`}
          onClick={() => navigate(`/projects/${timer.issue.key.split("-")[0]}/issues/${timer.issue.key}`)}
          onDelete={async () => { try { await pm.stopTimer({}); setTimer(null); ctx.notify("Time logged"); } catch (err) { ctx.fail(err); } }} />
      )}
      {ctx.canCreateProject && <Button variant="outlined" startIcon={<FolderKanban size={16} />} sx={ghostButtonSx} onClick={() => navigate("/projects?create=1")}>New project</Button>}
      <Button variant="contained" startIcon={<Plus size={16} />} sx={primaryButtonSx} onClick={() => setCreateOpen(true)}>Create</Button>
      <CreateIssueDialog open={createOpen} onClose={() => setCreateOpen(false)} project={project} onCreated={onCreated ? (i) => { onCreated(i); } : undefined} />
    </Stack>
  );
};

export default PmTopBar;
